import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { Channel } from "stream-chat";

const CHAT_MESSAGE_SEARCH_QUERY_KEY = "CHAT_MESSAGE_SEARCH";

/** Stream searches whole words by prefix; one character would match almost everything. */
export const MESSAGE_SEARCH_MIN_LENGTH = 2;

/** Stream's per-request maximum; the counter shows "100+" when a cursor says there is more. */
const MESSAGE_SEARCH_PAGE_SIZE = 100;

export const channelMessageSearchKeys = {
  all: [CHAT_MESSAGE_SEARCH_QUERY_KEY] as const,
  byChannelAndTerm: (cid: string, term: string) => [
    ...channelMessageSearchKeys.all,
    cid,
    term,
  ],
};

export type ChannelMessageSearchResult = {
  /** Matching message ids, newest first — the order the navigator steps through. */
  messageIds: Array<string>;
  /** Stream returned a cursor: the conversation holds more matches than one page. */
  hasMore: boolean;
};

/**
 * Messages in ONE conversation whose text matches `term`, for "Find in conversation". Server-side
 * (Stream's message search, which needs search indexing on the `messaging` channel type in the
 * dashboard), so it covers history the list has not loaded. Thread replies are dropped: the
 * navigator jumps within the conversation window, where a reply does not exist (the only
 * replies are the payments under a multi-payer request, see IS_MANUAL_THREADING_ENABLED).
 */
export const useChannelMessageSearch = (channel: Channel, term: string) => {
  const isEnabled = term.length >= MESSAGE_SEARCH_MIN_LENGTH;

  return useQuery<ChannelMessageSearchResult>({
    queryKey: channelMessageSearchKeys.byChannelAndTerm(channel.cid, term),
    queryFn: async () => {
      const { results, next } = await channel.search(term, {
        sort: [{ created_at: -1 }],
        limit: MESSAGE_SEARCH_PAGE_SIZE,
      });
      return {
        messageIds: results
          .filter(({ message }) => !message.parent_id)
          .map(({ message }) => message.id),
        hasMore: !!next,
      };
    },
    enabled: isEnabled,
    // The previous term's hits stay on screen while the new term loads, so the counter does not
    // blink to nothing on every keystroke.
    placeholderData: keepPreviousData,
  });
};
